import { useMemo } from "react";
import { MapPin, type LucideIcon } from "lucide-react";
import type {
  Insured,
  Policy,
  PolicyAdditionalInsured,
  PolicyWaiverOfSubrogation,
} from "@shared/schema";
import { PowerUnitsSection } from "../assets/PowerUnitsSection";
import { TrailersSection } from "../assets/TrailersSection";
import { DriversSection } from "../assets/DriversSection";
import { AdditionalInsuredsSection } from "../assets/AdditionalInsuredsSection";
import { LossPayeesSection } from "../assets/LossPayeesSection";
import { WaiversOfSubrogationSection } from "../assets/WaiversOfSubrogationSection";
import {
  aggregateDrivers,
  aggregateTrailers,
  aggregateVehicles,
} from "../assets/asset-helpers";

interface Props {
  policy: Policy;
  insured: Insured;
}

// CSR-1145 — the Policy detail Assets tab now reuses the same editable
// sections as the Insured Assets tab, scoped to this single policy. Adding a
// Power Unit / Trailer / Driver here writes to the policy's JSONB arrays, so
// the asset is linked to the policy and surfaces as Active on the insured
// Assets tab (which aggregates across all the insured's policies).
export function PolicyAssetsTab({ policy, insured }: Props) {
  const policies = useMemo(() => [policy], [policy]);
  const powerUnits = useMemo(() => aggregateVehicles(policies), [policies]);
  const trailers = useMemo(() => aggregateTrailers(policies), [policies]);
  const drivers = useMemo(() => aggregateDrivers(policies), [policies]);

  return (
    <div className="space-y-6" data-testid="policy-assets">
      <PowerUnitsSection powerUnits={powerUnits} policies={policies} />
      <TrailersSection trailers={trailers} policies={policies} />
      <DriversSection drivers={drivers} policies={policies} />
      <AddressesSection insured={insured} />
      <AdditionalInsuredsSection
        insuredId={insured.id}
        variant="policy"
        policyId={policy.id}
        policyAdditionalInsureds={
          policy.additionalInsureds as PolicyAdditionalInsured[] | null
        }
        policies={[policy]}
      />
      {/* CSR-804 — Loss Payees parity: real section, scoped to this policy. */}
      <LossPayeesSection
        insuredId={insured.id}
        policies={[policy]}
        policyId={policy.id}
        variant="policy"
      />
      {/* CSR-1101 — Waivers of Subrogation parity: real section, scoped to this policy. */}
      <WaiversOfSubrogationSection
        insuredId={insured.id}
        policies={[policy]}
        policyId={policy.id}
        policyWaiversOfSubrogation={
          policy.waiversOfSubrogation as PolicyWaiverOfSubrogation[] | null
        }
        variant="policy"
      />
    </div>
  );
}

function SectionHeader({
  icon: Icon,
  title,
  count,
}: {
  icon: LucideIcon;
  title: string;
  count: number;
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-border">
      <div className="flex items-center gap-2">
        <Icon className="w-5 h-5 text-foreground" aria-hidden="true" />
        <h3 className="text-base font-semibold">{title}</h3>
      </div>
      <span className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2 rounded-full bg-muted text-xs font-medium text-muted-foreground">
        {count}
      </span>
    </div>
  );
}

interface AddressLine {
  key: string;
  label: string;
  lines: string[];
}

function AddressesSection({ insured }: { insured: Insured }) {
  const garagingLines = formatAddressLines(
    insured.phyStreet,
    insured.phyCity,
    insured.phyState,
    insured.phyZip,
  );
  const mailingLines = formatAddressLines(
    insured.mailingStreet,
    insured.mailingCity,
    insured.mailingState,
    insured.mailingZip,
  );

  const addresses: AddressLine[] = [
    {
      key: "policy-garaging",
      label: "Policy / Garaging",
      lines: garagingLines,
    },
    { key: "mailing", label: "Mailing", lines: mailingLines },
    { key: "business", label: "Business", lines: [] },
    { key: "owner-home", label: "Owner Home", lines: [] },
  ];

  return (
    <section className="rounded-xl border border-border bg-card overflow-hidden">
      <SectionHeader
        icon={MapPin}
        title="Addresses"
        count={addresses.filter((a) => a.lines.length > 0).length}
      />
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {addresses.map((addr) => (
          <AddressCard key={addr.key} address={addr} />
        ))}
      </div>
    </section>
  );
}

function AddressCard({ address }: { address: AddressLine }) {
  return (
    <div
      className="rounded-xl border border-border p-4"
      data-testid={`policy-address-${address.key}`}
    >
      <p className="text-sm font-semibold text-foreground">{address.label}</p>
      {address.lines.length === 0 ? (
        <p className="text-sm text-muted-foreground mt-1">No current address</p>
      ) : (
        <div className="mt-1 text-sm text-foreground space-y-0.5">
          {address.lines.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function formatAddressLines(
  street?: string | null,
  city?: string | null,
  state?: string | null,
  zip?: string | null,
): string[] {
  const lines: string[] = [];
  if (street) lines.push(street);
  const cityStateZip = [city, [state, zip].filter(Boolean).join(" ").trim()]
    .filter(Boolean)
    .join(", ");
  if (cityStateZip) lines.push(cityStateZip);
  return lines;
}
